import React, { useCallback, useEffect, useRef, useState } from "react";
import { AlertRule } from "../../../../util";
import { DashboardsInfo } from "../../Dashboards/ViewDashboard";
import { fetchPanelData } from "../../../../BytebeamClient";
import { DashboardType } from "../../Dashboards/EditDashboardModal";
import uuid from "uuid";
import { FetchParams } from "../../Dashboards/Panel/util";
import { AlertsData } from "../../Dashboards/Panel/Alerts/PanelDef";
import {
  RelativeTimeRange,
  TimeRange,
  deserializeTimeRange,
} from "../../Dashboards/Datetime/TimeRange";
import LoadingAnimation from "../../../common/Loader";
import { CardContainer } from "../../../common/ActionsUtils";
import { ErrorMessage } from "../../../common/ErrorMessage";
import { DateTimeDropdown } from "../../Dashboards/Datetime/DateTimeDropdown";
import { DateTimeDropdownWrapper } from "../../Actions/Logs/LogsManagement";
import styled from "styled-components";
import Toggle from "../../../common/Toggle";
import {
  useStatusBarContext,
  StatusBarAutoRefreshDetailsType,
} from "../../../../context/StatusBar.context";
import { PaginationProps, Dropdown, DropdownProps } from "semantic-ui-react";
import { withRouter, RouteComponentProps } from "react-router-dom";
import AlertsComponent, {
  SharedAlertsViewProps,
  AlertsViewContext,
} from "./AlertsComponent";

//interface for AlertsDashboard component props
//receives data from parent route component
type AlertsDashboardProps = {
  readonly alertRules: AlertRule[]; // All available alert rules
  readonly alertGroups: string[]; // All available alert groups
  readonly dashboards: DashboardsInfo[]; // Available dashboards for navigation
} & RouteComponentProps;

//styled components for Alerts Dashboard
const AlertsDashboardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  margin-bottom: 42px;
`;

const FiltersContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const FilterDropdownContainer = styled.div`
  min-width: 250px;
  margin-right: auto;
`;

function AlertsDashboard(props: AlertsDashboardProps) {
  const { alertRules, alertGroups, dashboards, history, location } = props;

  // State management for dashboard-specific features
  const [panelData, setPanelData] = useState<AlertsData | null>(null);
  const [timeRange, setTimeRange] = useState<TimeRange>(
    new RelativeTimeRange(5, "minutes")
  );
  const [filterBys, setFilterBys] = useState({}); // eslint-disable-line @typescript-eslint/no-unused-vars

  // Tab and grouping state (shared concepts with ViewAlerts.tsx)
  const [activeTab, setActiveTab] = useState<string>("All Alerts");
  const [groupByAlertGroups, setGroupByAlertGroups] = useState<boolean>(false);
  const [activePage, setActivePage] = useState<number>(1);
  const [alertsPerPage, setAlertsPerPage] = useState<number>(10);

  //filter state
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);

  // Loading and error state
  const [loading, setLoading] = useState<boolean>(true);

  // Dashboard-specific auto-refresh
  const abortControllerRef = useRef(new AbortController());
  const isMountedRef = useRef<boolean>(true);
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshInterval = 5000; // in milliseconds : 5 seconds
  //state to track initial loading phase
  const [initialLoading, setInitialLoading] = useState(true);
  const shouldContinuePollingRef = useRef<boolean>(true);
  // Flag to prevent URL updates during initial state sync from URL
  const initialLoadRef = useRef<boolean>(true);
  const { setAutoRefresh } = useStatusBarContext();

  type FilterState = { filter: string; type: string };

  const updateUrlWithFilters = () => {
    // Skip URL update during initial load
    if (initialLoadRef.current) return;

    const urlParams = new URLSearchParams(location.search);

    const filtersToUpdate: FilterState[] = selectedFilters.map((filter) => ({
      filter,
      type: groupByAlertGroups ? "group" : "rule",
    }));

    if (filtersToUpdate.length > 0) {
      const filtersStr = JSON.stringify(filtersToUpdate);
      urlParams.set("filters", filtersStr);
    } else {
      urlParams.delete("filters");
    }
    if (groupByAlertGroups) {
      urlParams.set("groupBy", "groups");
    } else {
      urlParams.delete("groupBy");
    }
    if (activeTab !== "All Alerts") {
      urlParams.set("tab", activeTab);
    } else {
      urlParams.delete("tab");
    }
    if (activePage !== 1) {
      urlParams.set("page", activePage.toString());
    } else {
      urlParams.delete("page");
    }
    const serializedTimeRange = timeRange.serialize();
    urlParams.set("timeRange", JSON.stringify(serializedTimeRange));

    history.replace({
      pathname: location.pathname,
      search: urlParams.toString(),
    });
  };

  const loadFiltersFromUrl = () => {
    const urlParams = new URLSearchParams(location.search);

    const groupByParam = urlParams.get("groupBy");
    if (groupByParam === "groups") {
      setGroupByAlertGroups(true);
    } else {
      setGroupByAlertGroups(false);
    }

    try {
      const filtersParam = urlParams.get("filters");
      if (filtersParam) {
        const parsedFilters = JSON.parse(filtersParam) as FilterState[];
        const filterValues = parsedFilters.map((f) => f.filter);
        setSelectedFilters(filterValues);

        // If no explicit groupBy param, fall back to inferring from filters
        if (!groupByParam && parsedFilters.length > 0) {
          setGroupByAlertGroups(parsedFilters[0].type === "group");
        }
      }

      const timeRangeParam = urlParams.get("timeRange");
      if (timeRangeParam) {
        const serializedTimeRange = JSON.parse(timeRangeParam);
        const newTimeRange = deserializeTimeRange(serializedTimeRange);
        setTimeRange(newTimeRange);
      }

      const tabParam = urlParams.get("tab");
      if (tabParam) {
        setActiveTab(tabParam);
      }

      const pageParam = urlParams.get("page");
      if (pageParam) {
        setActivePage(parseInt(pageParam, 10));
      }
    } catch (e) {
      console.error("Error parsing filters from URL:", e);
    }

    setTimeout(() => {
      initialLoadRef.current = false;
    }, 50); // Delaying to prevent URL update during initial state sync from URL
  };

  // Load filters from URL on component mount
  useEffect(() => {
    loadFiltersFromUrl();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update URL when filter state changes
  useEffect(() => {
    updateUrlWithFilters();
  }, [selectedFilters, groupByAlertGroups, activeTab, activePage, timeRange]); // eslint-disable-line react-hooks/exhaustive-deps

  const onTimeRangeChange = (timeRange: TimeRange) => {
    setTimeRange(timeRange);
  };

  // filter dropdown options are memoized to prevent unnecessary re-renders
  const memoizedAlertGroups = React.useMemo(
    () => [...alertGroups],
    [alertGroups]
  );
  const memoizedAlertRules = React.useMemo(() => [...alertRules], [alertRules]);

  const dropdownOptions = React.useMemo(() => {
    if (groupByAlertGroups) {
      // For alert groups
      return memoizedAlertGroups.map((group) => ({
        key: group,
        text: group,
        value: group,
      }));
    } else {
      // For alert rules
      return memoizedAlertRules.map((rule) => ({
        key: rule.id,
        text: rule.name,
        value: rule.name,
      }));
    }
  }, [memoizedAlertGroups, memoizedAlertRules, groupByAlertGroups]);

  const handleFilterChange: DropdownProps["onChange"] = (_, data) => {
    setSelectedFilters(data.value as string[]);
    setActiveTab("All Alerts");
    setActivePage(1);
  };

  const handleClearFilters = () => {
    setSelectedFilters([]);
    setActiveTab("All Alerts");
    setActivePage(1);
  };

  const handlePageChange = async (
    event: React.MouseEvent<HTMLAnchorElement>,
    data: PaginationProps
  ) => {
    const POLLING_RESUME_DELAY_MS = 1000;

    if (data.activePage) {
      setActivePage(data.activePage as number);

      // Pause polling during manual navigation
      shouldContinuePollingRef.current = false;
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
      setTimeout(() => {
        shouldContinuePollingRef.current = true;
      }, POLLING_RESUME_DELAY_MS);
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    shouldContinuePollingRef.current = true;

    return () => {
      // Cleanup on unmount
      isMountedRef.current = false;
      shouldContinuePollingRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
      setAutoRefresh({
        lastRefreshedTime: null,
        isRefreshPaused: false,
        refreshPage: "",
        isRefreshStale: false,
      });
    };
  }, [setAutoRefresh]);

  useEffect(() => {
    // Cancel previous polling
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    shouldContinuePollingRef.current = true;

    const refreshData = async (
      page: number = activePage,
      isAutoRefresh: boolean = false
    ) => {
      if (isMountedRef.current && !isAutoRefresh) {
        setLoading(true);
      }

      try {
        // Create new abort controller
        abortControllerRef.current = new AbortController();

        let activeAlertRules = alertRules.map((rule) => rule.name);
        if (activeTab !== "All Alerts")
          activeAlertRules = groupByAlertGroups
            ? alertRules.flatMap((rule) =>
                rule.group === activeTab ? [rule.name] : []
              )
            : [activeTab];
        else {
          if (selectedFilters.length > 0) {
            activeAlertRules = !groupByAlertGroups
              ? [...selectedFilters]
              : alertRules.flatMap((rule) =>
                  selectedFilters.includes(rule.group) ? [rule.name] : []
                );
          } else activeAlertRules = alertRules.map((rule) => rule.name);
        }

        const filter = groupByAlertGroups
          ? { alert_groups: selectedFilters }
          : { alert_names: selectedFilters };

        //building dashboard-specific panel metadata
        const panelMeta = {
          type: "alerts",
          id: `alerts-dashboard-page-panel-${uuid.v4()}`,
          title: "Alerts Dashboard Panel",
          description: "Alerts Dashboard in alerts Tab",
          aggregates: [], // Dashboard doesn't use aggregates
          tabbedView: true, // Dashboard always uses tabs
          tabByAlertGroups: groupByAlertGroups,
          tabList: activeTab === "All Alerts" ? "all" : activeTab,
          page: page,
          limit: alertsPerPage,
          groupBys: ["id"],
          alert_rules: activeAlertRules,
          ...(selectedFilters.length > 0 ? filter : {}),
        };

        const fetchParams: FetchParams = {
          groupBys: [],
          fetchAll: false,
          timeRange: timeRange,
          filterBys: filterBys,
        };

        const panelsData = await fetchPanelData(
          "alerts-dashboard-page",
          panelMeta.id,
          [panelMeta],
          fetchParams,
          abortControllerRef.current.signal,
          DashboardType.FleetDashboard
        );

        if (isMountedRef.current) {
          let data = await panelsData?.[0]?.data;

          setPanelData(data ?? null);
          setInitialLoading(false);

          setAutoRefresh({
            lastRefreshedTime: new Date(),
            isRefreshPaused: false,
            refreshPage: "alerts-dashboard",
            isRefreshStale: false,
          } as StatusBarAutoRefreshDetailsType);
        }
      } catch (error) {
        if (
          isMountedRef.current &&
          !(error instanceof Error && error.name === "AbortError")
        ) {
          console.error(error);
          setInitialLoading(false);
        }
      } finally {
        if (isMountedRef.current && !isAutoRefresh) {
          setLoading(false);
        }

        if (timeoutIdRef.current) {
          clearTimeout(timeoutIdRef.current);
        }

        // Continue auto-refresh cycle
        if (isMountedRef.current && shouldContinuePollingRef.current) {
          timeoutIdRef.current = setTimeout(async () => {
            await refreshData(activePage, true);
          }, refreshInterval);
        }
      }
    };

    refreshData();

    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterBys,
    timeRange,
    groupByAlertGroups,
    activeTab,
    selectedFilters,
    activePage,
    alertsPerPage,
  ]);

  // Load saved alerts per page setting
  useEffect(() => {
    try {
      const savedAlertsPerPage =
        window.localStorage.getItem("alertsListPerPage");
      if (savedAlertsPerPage) {
        const parsedValue = Number(savedAlertsPerPage);
        if (!isNaN(parsedValue) && parsedValue > 0) {
          setAlertsPerPage(parsedValue);
        }
      }
    } catch (error) {
      console.error("Failed to read alerts per page from localStorage:", error);
    }
  }, [setAlertsPerPage]);

  const handleAlertsPerPageChange = useCallback((event: any, data: any) => {
    setAlertsPerPage(data.value);
    setActivePage(1);
    window.localStorage.setItem("alertsListPerPage", data.value);
  }, []);

  //renderContent function handles the main content rendering logic
  // It checks loading state, error state, and prepares data for AlertsComponent
  const renderContent = () => {
    if (initialLoading || loading) {
      return (
        <LoadingAnimation
          fontSize="16px"
          loaderContainerHeight="60vh"
          loaderSize="42px"
          loadingText={`Loading Alerts...`}
        />
      );
    }

    // Error handling for no alerts rules or empty data
    if (!alertRules?.length || !panelData) {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
          }}
        >
          <ErrorMessage
            message="No alerts data found, adjust your filters or verify your alert settings"
            iconSize="large"
          />
        </div>
      );
    }

    // Based on selected filters and grouping prepare filtered data for AlertsComponent
    let filteredData = { ...panelData };
    let filteredAlertGroups = [...alertGroups];

    if (selectedFilters.length > 0) {
      filteredData = {
        ...panelData,
        alerts: panelData.alerts.filter((alert) => {
          if (groupByAlertGroups) {
            const rule = panelData.alertRules?.find(
              (r) => r.id === alert.alert_rule.id
            );
            return rule && selectedFilters.includes(rule.group);
          } else {
            return selectedFilters.includes(alert.alert_rule.name);
          }
        }),
        alertRules: panelData.alertRules?.filter((rule) => {
          if (groupByAlertGroups) {
            return selectedFilters.includes(rule.group);
          } else {
            return selectedFilters.includes(rule.name);
          }
        }),
      };

      if (groupByAlertGroups) {
        filteredAlertGroups = alertGroups.filter((group) =>
          selectedFilters.includes(group)
        );
      }
    }

    //prepare shared props for AlertsComponent
    const sharedProps: SharedAlertsViewProps = {
      data: filteredData,
      loading,
      alertGroups: filteredAlertGroups,
      aggregates: [], // Dashboard doesn't use aggregates
      groupByAlertGroups,
      activePage,
      setActivePage,
      totalPages: Math.ceil(filteredData.totalCount / alertsPerPage),
      alertsPerPage,
      setAlertsPerPage,
      changeAlertsPerPage: handleAlertsPerPageChange,
      handlePageChange,
      activeTab,
      setActiveTab,
      dashboards,
      selectedFilters, // DASHBOARD-SPECIFIC: filter state
      tabbedView: true, // Dashboard always uses tabs
      tables: {}, // Dashboard uses basic formatting
    };

    //preparing dashboard context for AlertsComponent
    const context: AlertsViewContext = {
      isPanel: false, // DASHBOARD CONTEXT: standalone page
      timeRange, // DASHBOARD CONTEXT: time range control
      onTimeRangeChange, // DASHBOARD CONTEXT: time range handler
    };

    //rendering alerts component with shared props and context with dashboard-specific features
    return <AlertsComponent {...sharedProps} context={context} />;
  };

  // Main render function for AlertsDashboard
  return (
    <CardContainer>
      <AlertsDashboardHeader>
        <FiltersContainer>
          <FilterDropdownContainer>
            <Dropdown
              placeholder={
                groupByAlertGroups
                  ? "Filter by Alert Groups"
                  : "Filter by Alert Rules"
              }
              fluid
              multiple
              search
              selection
              clearable
              options={dropdownOptions}
              value={selectedFilters}
              onChange={(e, data) => {
                if (
                  !data.value ||
                  (Array.isArray(data.value) && data.value.length === 0)
                ) {
                  handleClearFilters();
                } else {
                  handleFilterChange(e, data);
                }
              }}
              disabled={loading}
              onClear={handleClearFilters}
            />
          </FilterDropdownContainer>

          <div>
            <span style={{ marginRight: "8px" }}>Group by Alert Groups</span>
            <Toggle
              id="group-by-alert-groups"
              size="medium"
              checked={groupByAlertGroups}
              onChange={() => {
                setGroupByAlertGroups((prev) => !prev);
                setSelectedFilters([]);
                setActiveTab("All Alerts");
                setActivePage(1);
              }}
              style={{ top: "4px" }}
            />
          </div>
        </FiltersContainer>

        <DateTimeDropdownWrapper>
          <DateTimeDropdown
            showControls={true}
            timeRange={timeRange}
            onTimeRangeChange={onTimeRangeChange}
            disabled={loading}
          />
        </DateTimeDropdownWrapper>
      </AlertsDashboardHeader>

      {/* SHARED CONTENT AREA */}
      {/* Both alerts panel and AlertsDashboard render AlertsComponent here */}
      {renderContent()}
    </CardContainer>
  );
}

export default withRouter(AlertsDashboard);
